import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { getErrorMessage, getRequest, PROJECTS_API } from "./api";
import { PageShell, panelSx, statusChip, SummaryCard } from "./ProjectsCommon";

function DashboardSkeleton() {
  const cardSkeletons = Array.from({ length: 4 });
  const rowSkeletons = Array.from({ length: 4 });

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
        }}
      >
        {cardSkeletons.map((_, index) => (
          <Box key={index} sx={{ ...panelSx, p: 2.2, minHeight: 142 }}>
            <Skeleton variant="text" width="45%" height={24} />
            <Skeleton variant="text" width="34%" height={42} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="82%" height={22} sx={{ mt: 1 }} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        {[0, 1].map((panel) => (
          <Box
            key={panel}
            sx={{
              flex: 1,
              minWidth: 0,
              width: { xs: "100%", md: "calc(50% - 8px)" },
            }}
          >
            <Box sx={{ ...panelSx, p: 2.5, height: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ flexWrap: "wrap", rowGap: 1 }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Skeleton variant="text" width="42%" height={32} />
                  <Skeleton variant="text" width="74%" height={22} />
                </Box>
                <Skeleton variant="rounded" width={126} height={36} sx={{ borderRadius: 2 }} />
              </Stack>

              <Stack spacing={1.4} sx={{ mt: 2 }}>
                {rowSkeletons.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: "1px solid rgba(226,232,240,0.9)",
                      backgroundColor: "#fff",
                      minHeight: panel === 0 ? "auto" : 110,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Skeleton variant="text" width="62%" height={26} />
                        <Skeleton variant="text" width="48%" height={20} />
                      </Box>
                      <Skeleton variant="rounded" width={92} height={26} sx={{ borderRadius: 999 }} />
                    </Stack>

                    {panel === 0 ? (
                      <>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mt: 1.1 }}
                        >
                          <Skeleton variant="text" width="48%" height={20} />
                          <Skeleton variant="text" width={36} height={20} />
                        </Stack>
                        <Skeleton variant="rounded" width="100%" height={8} sx={{ mt: 0.8, borderRadius: 999 }} />
                      </>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

export default function ProjectsDashboard() {
  const navigate = useNavigate();
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);

  const loadData = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const [summaryData, projectData, siteData] = await Promise.all([
        getRequest(`${PROJECTS_API}/summary`, token, activeOrg),
        getRequest(`${PROJECTS_API}/deployments`, token, activeOrg),
        getRequest(`${PROJECTS_API}/sites`, token, activeOrg),
      ]);
      setSummary(summaryData);
      setProjects(projectData?.projects || []);
      setSites(siteData?.sites || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load project dashboard"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <PageShell
      title="Projects"
      subtitle="Track customer deployments, rollout progress, and site readiness before field commissioning starts."
      actions={
        <Stack spacing={1}>
          <Typography sx={{ color: "rgba(226,232,240,0.82)", fontSize: 13, fontWeight: 700 }}>
            Rollout focus
          </Typography>
          <Typography sx={{ color: "#F8FAFC", fontSize: 24, fontWeight: 900 }}>
            {summary?.active_projects || 0} active programs
          </Typography>
        </Stack>
      }
    >
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {loading ? (
        <DashboardSkeleton />
      ) : (
        <Stack spacing={3}>
         <Box
  sx={{
    display: "grid",
    gap: 2,

    // Responsive columns
    gridTemplateColumns: {
      xs: "1fr",                // mobile → 1 column
      sm: "repeat(2, 1fr)",    // small → 2 columns
      md: "repeat(3, 1fr)",    // tablet → 3 columns
      lg: "repeat(4, 1fr)",    // desktop → 4 columns
    },
  }}
>
  <SummaryCard
    title="Customers"
    value={summary?.customers || 0}
    subtitle="Deployment customers under the active org"
  />

  <SummaryCard
    title="Projects"
    value={summary?.projects || 0}
    subtitle="Programs and rollout phases being tracked"
  />

  <SummaryCard
    title="Sites"
    value={summary?.sites || 0}
    subtitle="Deployment locations across all programs"
  />

  <SummaryCard
    title="Planned Devices"
    value={summary?.planned_devices || 0}
    subtitle={`${summary?.commissioned_devices || 0} already marked commissioned`}
  />
</Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              justifyContent: "center",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                width: { xs: "100%", md: "calc(50% - 8px)" },
              }}
            >
              <Box sx={{ ...panelSx, p: 2.5, height: "100%" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ flexWrap: "wrap", rowGap: 1 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0F172A" }}>
                      Active Projects
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748B" }}>
                      Progress view across current deployment programs.
                    </Typography>
                  </Box>

                  <Button
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate("/projects/deployments")}
                    sx={{ textTransform: "none", fontWeight: 800, flexShrink: 0 }}
                  >
                    Open projects
                  </Button>
                </Stack>

                <Stack spacing={1.4} sx={{ mt: 2 }}>
                  {projects.length ? (
                    projects.slice(0, 6).map((project) => {
                      const progress = project.summary?.progress_percent || 0;

                      return (
                        <Box
                          key={project.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: "1px solid rgba(226,232,240,0.9)",
                            backgroundColor: "#fff",
                            minWidth: 0,
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={1.5}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 800,
                                  color: "#0F172A",
                                  wordBreak: "break-word",
                                }}
                              >
                                {project.name}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.35,
                                  fontSize: 12.5,
                                  color: "#64748B",
                                  wordBreak: "break-word",
                                }}
                              >
                                {project.customer?.name || "Customer pending"} / {project.code}
                              </Typography>
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>{statusChip(project.status)}</Box>
                          </Stack>

                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mt: 1.1 }}
                          >
                            <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                              {project.commissioned_devices || 0} of {project.target_devices || 0} commissioned
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#0F172A",
                                flexShrink: 0,
                              }}
                            >
                              {progress}%
                            </Typography>
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                              mt: 0.8,
                              height: 8,
                              borderRadius: 999,
                              backgroundColor: "#E2E8F0",
                            }}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography sx={{ color: "#64748B" }}>No projects created yet.</Typography>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                width: { xs: "100%", md: "calc(50% - 8px)" },
              }}
            >
              <Box sx={{ ...panelSx, p: 2.5, height: "100%" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ flexWrap: "wrap", rowGap: 1 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0F172A" }}>
                      Recent Sites
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748B" }}>
                      Newest deployment locations prepared for field execution.
                    </Typography>
                  </Box>

                  <Button
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate("/projects/sites")}
                    sx={{ textTransform: "none", fontWeight: 800, flexShrink: 0 }}
                  >
                    Open sites
                  </Button>
                </Stack>

                <Stack spacing={1} sx={{ mt: 2 }}>
                  {sites.length ? (
                    sites.slice(0, 6).map((site) => (
                      <Box
                        key={site.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          border: "1px solid rgba(226,232,240,0.9)",
                          backgroundColor: "#fff",
                          minHeight: 110,
                          minWidth: 0,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: "#0F172A",
                                wordBreak: "break-word",
                              }}
                            >
                              {site.name}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.35,
                                fontSize: 12.5,
                                color: "#64748B",
                                wordBreak: "break-word",
                              }}
                            >
                              {site.project?.name || "Project pending"} / {site.city || "City not set"}
                            </Typography>
                          </Box>

                          <Box sx={{ flexShrink: 0 }}>{statusChip(site.status)}</Box>
                        </Stack>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ color: "#64748B" }}>
                      No deployment sites created yet.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>
          </Box>

        </Stack>
      )}
    </PageShell>
  );
}
